"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.isShell = isShell;
exports.getSPAVersion = getSPAVersion;
exports.addVersionToMetricsConfig = addVersionToMetricsConfig;
exports.generateShellUrl = generateShellUrl;

/**
 * Checks if current window is Unified Shell or iframe.
 * @returns true if running in Shell, false if running in an iframe
 */
function isShell() {
  const w = window;
  return w.parent === w || 'config' in w && w.config.tis === 1;
}

function getSPAVersion() {
  var _w$config;

  const w = window;
  return (_w$config = w.config) === null || _w$config === void 0 ? void 0 : _w$config.spaVersion;
}

function addVersionToMetricsConfig(config) {
  const version = getSPAVersion();

  if (version && config.application) {
    config = { ...config,
      application: { ...config.application,
        version
      }
    };
  }

  return config;
}

function splitPath(path, separator) {
  const startsWithSlash = path.startsWith('/');
  const idx = path.indexOf(separator);
  const res = idx > -1 ? path.slice(idx + separator.length) : path;
  return res.startsWith('/') || !startsWithSlash ? res : `/${res}`;
}
/**
 * Called when receiving a message from the parent frame.
 * @param config The current external configuration.
 * @param location A ShellUrlObject to describe the location lookup.
 * @returns A valid full shell URL (as a string).
 */


function generateShellUrl(config, location, newApp) {
  let locationHref = location.href || '';
  const {
    baseFrameUrl,
    discovery,
    externalQueryParams,
    historyType
  } = config;
  const baseUrl = new URL(config.baseUrl);
  const isHashHistory = historyType === 'HASH';
  const configUrl = new URL(baseFrameUrl);
  const shellUrl = new URL(baseUrl.origin);
  const {
    path,
    sandbox
  } = location; // If the href is already a shell URL, there is nothing more to do.

  if (/^https:\/\/experience(-qa|-stage)?.adobe.com/.test(locationHref)) {
    const windowUrl = new URL(locationHref);
    windowUrl.search += `${windowUrl.search ? '&' : ''}${externalQueryParams.replace(/^\?/, '')}`;
    return windowUrl.toString();
  }

  if (!locationHref) {
    // Solutions URLs are "mounted" at the URL provided by the solution config
    // or by the discovery endpoint. The `baseFrameUrl` is the root URL that is
    // mounted at the `/@tenant/solution` path. If `baseFrameUrl` came from a
    // discovery call, it will be a fully formed path to a page with their
    // application. We can't just append the new path to that URL and instead
    // must treat the path as an absolute path (e.g. completely replacing the
    // existing path). In the non-discovery case, the `baseFrameUrl` is a
    // non-fully formed path within the application (e.g. /solutions/ddam). Any
    // new path should be appended to the `baseFrameUrl`'s path.
    const base = discovery ? configUrl.origin : baseFrameUrl;
    let locationPath = path || ''; // Instead of updating the current shellUrl, newApp generates a shellUrl
    // for use in opening a window or tab for another solution with the provided
    // path while maintaining the query parameters present in the current url.

    if (newApp) {
      const tenant = `/@${config.tenant}`;
      shellUrl.hash = sandbox ? `${tenant}/sname:${sandbox}${locationPath}` : `${tenant}${locationPath}`;
      shellUrl.search = externalQueryParams;
      return shellUrl.toString();
    }

    locationPath = isHashHistory && locationPath.indexOf('#') === -1 ? `/#${locationPath}` : locationPath || '/';
    locationHref = `${base}${locationPath}`;
  }

  const url = new URL(locationHref);
  let pathname = isHashHistory ? url.hash.replace(/^#\//, '/') : url.pathname;
  let hash = baseUrl.hash; // Split baseUrl.hash by tenant and solution path to insert sandbox.

  if (sandbox) {
    const matches = baseUrl.hash.match(`^#(/@[^/]*)?(/sname:[^/]+)?(/[^/]+)`);

    if (matches) {
      const tenant = matches[1]; // matches[2] will be the sandbox if it exists in the baseUrl

      const solutionPath = matches[3];
      hash = `#${tenant}/sname:${sandbox}${solutionPath}`;
    }
  }

  pathname = `${hash}${discovery ? pathname : splitPath(pathname, configUrl.pathname)}`; // Need to preserve the search and hash values on the URL being loaded.

  shellUrl.hash = `${pathname}${url.search}${isHashHistory ? '' : url.hash}`;
  shellUrl.search = externalQueryParams;
  return shellUrl.toString();
}